/**
 * Trunnion AI - WebSocket Hook
 * Manages Socket.io connection lifecycle.
 */

import { useEffect, useRef, useCallback } from 'react';
import { getSocketClient } from '@/lib/websocket';
import { useAuthStore } from '@/stores/authStore';
import { WSEvent } from '@/types';
import type { Socket } from 'socket.io-client';

export function useWebSocket() {
  const socketRef = useRef<Socket | null>(null);
  const { token, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const socket = getSocketClient(token);
    socketRef.current = socket;

    socket.on(WSEvent.CONNECT, () => {
      console.log('[WS] Connected to Trunnion AI server');
    });

    socket.on(WSEvent.DISCONNECT, (reason: string) => {
      console.log('[WS] Disconnected:', reason);
    });

    socket.on(WSEvent.ERROR, (error: unknown) => {
      console.error('[WS] Error:', error);
    });

    socket.on(WSEvent.AUTHENTICATED, () => {
      console.log('[WS] Authenticated successfully');
    });

    return () => {
      socket.off(WSEvent.CONNECT);
      socket.off(WSEvent.DISCONNECT);
      socket.off(WSEvent.ERROR);
      socket.off(WSEvent.AUTHENTICATED);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, isAuthenticated]);

  const subscribe = useCallback(
    <T>(event: WSEvent, handler: (data: T) => void) => {
      const socket = socketRef.current;
      if (!socket) return () => {};

      socket.on(event, handler as (...args: unknown[]) => void);
      return () => {
        socket.off(event, handler as (...args: unknown[]) => void);
      };
    },
    [],
  );

  const emit = useCallback((event: string, data?: unknown) => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit(event, data);
  }, []);

  return {
    socket: socketRef.current,
    subscribe,
    emit,
    isConnected: socketRef.current?.connected ?? false,
  };
}
